import React, { useState, useEffect } from "react";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  makeStyles,
} from "@material-ui/core";
import GeneralModal from "./GeneralModal";
import { TextFieldInput } from "../components-atoms";
import { ButtonTextPrimary, ButtonPrimary } from "../components-atoms";
import { editMerchantUser, EditMerchantUserData } from "../redux/actions/merchantDetails";
import { MerchantOrganisationUserDetails } from "../redux/actions/merchantDetails";

interface IProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  user: MerchantOrganisationUserDetails | null;
  merchantId: string;
}

export const EditMerchantUserModal = ({
  visible,
  onClose,
  onSuccess,
  user,
  merchantId
}: IProps) => {
  const classes = useStyles();
  const [formData, setFormData] = useState<EditMerchantUserData>({
    Email: "",
    FirstName: "",
    LastName: "",
    PhoneNumber: "",
    ShouldSendEmailNotification: false,
    ShouldSendNotification: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setFormData({
        Email: user.Email || "",
        FirstName: user.FirstName || "",
        LastName: user.LastName || "",
        PhoneNumber: user.PhoneNumber || "",
        ShouldSendEmailNotification: user.ShouldSendEmailNotification || false,
        ShouldSendNotification: user.ShouldSendNotification || false,
      });
    }
  }, [user]);

  const handleInputChange = (field: keyof EditMerchantUserData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox' 
      ? event.target.checked 
      : event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const onFormSubmit = () => {
    if (!isFormValid() || !user) {
      return;
    }

    setLoading(true);
    setError(null);

    const requestData = {
      ...formData
    };
    
    editMerchantUser(
      merchantId,
      user.Id,
      requestData,
      (response) => {
        setLoading(false);
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      },
      (err) => {
        setLoading(false);
        
        // Handle different error structures
        let errorMessage = 'Failed to update user. Please try again.';
        
        if (typeof err === 'string') {
          errorMessage = err;
        } else if (err?.response?.data?.Error) {
          errorMessage = err.response.data.Error;
        } else if (err?.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err?.message) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
      }
    );
  };

  const handleClose = () => {
    // Reset form data
    setFormData({
      Email: "",
      FirstName: "",
      LastName: "",
      PhoneNumber: "",
      ShouldSendEmailNotification: false,
      ShouldSendNotification: false,
    });
    setError(null);
    setLoading(false);
    onClose();
  };

  const isFormValid = () => {
    return (
      !!formData.FirstName?.trim() &&
      !!formData.LastName?.trim() &&
      !!formData.Email?.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email || "")
    );
  };

  return (
    <GeneralModal
      title="Edit Merchant User"
      visible={visible}
      onClose={handleClose}
    >
      <form>
        <GeneralModal.ContentArea>
          <fieldset className={classes.fieldset}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <TextFieldInput
                  label="First Name"
                  value={formData.FirstName}
                  onChange={handleInputChange('FirstName')}
                  placeholder="Enter first name"
                  required
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextFieldInput
                  label="Last Name"
                  value={formData.LastName}
                  onChange={handleInputChange('LastName')}
                  placeholder="Enter last name"
                  required
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextFieldInput
                  label="Email Address"
                  type="email"
                  value={formData.Email}
                  onChange={handleInputChange('Email')}
                  placeholder="Enter email address"
                  required
                  disabled={loading}
                  error={!!error}
                  helperText={error}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextFieldInput
                  label="Phone Number"
                  value={formData.PhoneNumber || ""}
                  onChange={handleInputChange('PhoneNumber')}
                  placeholder="Enter phone number"
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox color="primary" checked={formData.ShouldSendEmailNotification} onChange={handleInputChange('ShouldSendEmailNotification')} />}
                  label="Receive Email Notification"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox color="primary" checked={formData.ShouldSendNotification} onChange={handleInputChange('ShouldSendNotification')} />}
                  label="Receive Phone Notification"
                />
              </Grid>
            </Grid>
          </fieldset>
        </GeneralModal.ContentArea>
        
        <GeneralModal.ButtonArea>
          <ButtonTextPrimary onClick={handleClose} disabled={loading}>
            Cancel
          </ButtonTextPrimary>
          <ButtonPrimary
            onClick={onFormSubmit}
            disabled={!isFormValid() || loading}
          >
            {loading ? "Updating..." : "Update User"}
          </ButtonPrimary>
        </GeneralModal.ButtonArea>
      </form>
    </GeneralModal>
  );
};

const useStyles = makeStyles({
  fieldset: {
    border: "none",
    padding: 0,
    margin: 0,
  },
});

export default EditMerchantUserModal;
